import { Helmet } from "react-helmet"
import { Link } from "react-router-dom"
import { MenuButton } from "@headlessui/react"
import React, { useEffect, useState } from "react"

import Config from "../../api/Config"
import { ASSETS } from "../../api/Registry"
import DropDown from "../../lib/fragments/DropDown"
import { getAssetRoute } from "../../utils/Helpers"
import HttpServices from "../../services/HttpServices"
import { Loading } from "../../components/utils/Loading"
import { SideMenu } from "../../components/layouts/SideMenu"
import { SimplifiedHeader } from "../../components/layouts/SimplifiedHeader"
import { G_onInputBlurHandler, G_onInputChangeHandler } from "../../components/utils/InputHandlers"

interface Filters {
    selected: any;
}

export const AssetRegistry = () => {
    const [state, setstate] = useState({
        posting: false,
        httpStatus: 200,
        status: 'pending',
        data: {
            categories: null,
        },
        input: {
            search: '',
            category: '00000000-0000-0000-0000-000000000001',
            filters: [],
        },
        errors: {
            search: '',
        }
    })

    const pageTitle = "ASMX"
    const categoryRoute = getAssetRoute('ASSET.CONFIG_')

    useEffect(() => {
        const fetchAssetCategories = async () => {
            let { status } = state
            let { httpStatus } = state

            let categories = null

            try {
                const response: any = await HttpServices.httpGet(ASSETS.ALL_CATEGORIES)
                httpStatus = response.status

                if (httpStatus === 200) {
                    const payload: any = response.data.payload

                    status = 'fulfilled'
                    categories = payload.categories
                } else {
                    status = 'rejected'
                }
            } catch (error) {
                status = 'rejected'
                httpStatus = 500
            }

            setstate((prevState) => {
                let filters = [...prevState.input.filters];

                if (categories) {
                    categories.forEach((category: any) => {
                        filters.push({ selected: category.uuid });
                    });
                }

                return {
                    ...prevState,
                    status,
                    httpStatus,
                    data: {
                        ...prevState.data,
                        categories: categories,
                    },
                    input: {
                        ...prevState.input,
                        filters
                    },
                }
            });
        }

        fetchAssetCategories()
    }, [])

    const onChangeHandler = (e: any) => {
        const { posting } = state

        if (!posting) {
            let { input } = state
            let { errors } = state
            let output: any = G_onInputChangeHandler(e)

            const updatedInput = {
                ...input,
                [e.target.name]: output.value
            };

            const updatedErrors = {
                ...errors,
                [e.target.name]: output.error
            };

            setstate((prev) => ({
                ...prev,
                input: updatedInput,
                errors: updatedErrors,
            }));
        }
    }

    const targetLength = (name: string) => {
        switch (name) {
            default:
                return {
                    min: 3,
                    max: 15
                };
        }
    }

    const onInputBlur = (e: any) => {
        const { posting } = state

        if (!posting) {
            let { input } = state
            let { errors } = state

            const target0 = targetLength(e.target.name)
            let output: any = G_onInputBlurHandler(e, '', target0.min, target0.max)

            const updatedInput = {
                ...input,
                [e.target.name]: output.value
            };

            const updatedErrors = {
                ...errors,
                [e.target.name]: output.error
            };

            setstate((prev) => ({
                ...prev,
                input: updatedInput,
                errors: updatedErrors,
            }));
        }
    }

    const handleCheckboxChange = (e: any, uuid: any) => {
        let { posting } = state

        if (!posting) {
            setstate((prevState) => {
                let isChecked = e.target.checked

                const updatedFilters = isChecked
                    ? [...prevState.input.filters, { selected: uuid }] // Add the new filter
                    : prevState.input.filters.filter((filter) => filter.selected !== uuid); // Remove the filter

                return {
                    ...prevState,
                    input: {
                        ...prevState.input,
                        filters: updatedFilters,
                    },
                };
            });
        }
    }

    return (
        <>
            <Helmet>
                <title>{pageTitle} - {Config.APP_NAME}</title>
            </Helmet>

            <div className="flex flex-row min-h-screen items-center">
                <SideMenu ActiveMNu={'ASMX'} />

                <div className="md:ml-16 flex-1 flex flex-col min-h-screen bg-theme from-[#ffffff] dark:from-slate-900 via-[#ffffff] dark:via-slate-700 via-[40%] dark:via-[60%] to-gradient dark:to-orange-900 gap-x-3 items-center md:px-0 px-4">
                    <div className="py-3 w-full flex-none bg-emeral-100">
                        <SimplifiedHeader />
                    </div>

                    <div className="flex-grow flex flex-col items-center justify-center relative w-full">
                        <section className="md:px-3.5 w-full md:w-[600px] mx-auto py-3 px-2 border-0 rounded-md absolute transform -translate-y-16">
                            {
                                state.status === 'rejected' ? (
                                    <>

                                    </>
                                ) : state.status === 'fulfilled' ? (
                                    <>
                                        <header className="landing-header pb-5">
                                            <div className="landing mb-0 text-left flex flex-col gap-y-2">
                                                <h2 className={`odyssey text-left text-muted text-xl`}>{pageTitle}</h2>
                                            </div>
                                        </header>

                                        <div className="header-title align-middle flex flex-nowrap title-transition">
                                            <h1 className="title md:text-3xl text-xl tracking-wider leading-9 md:pb-4 pb-3 bg-gradient-to-r from-orange-400 to-red-600 bg-clip-text text-transparent">
                                                <span className="block">Hi there,</span>
                                                What would you like to find?
                                            </h1>
                                        </div>

                                        <div className="w-full bg-white dark:bg-slate-900 md:p-2 py-1 px-2 rounded-md border border-slate-300 dark:border-slate-600">
                                            <div className="flex md:align-middle md:items-center flex-col md:flex-row mx-auto w-full rounded-lg gap-y-4">
                                                <div className="mx-auto flex-grow flex flex-row w-full items-center align-middle gap-x-2">
                                                    <input id="search" name="search" type="text" autoComplete="off" required className={`appearance-none rounded-md relative block w-full px-3 py-2 dark:bg-slate-900 md:py-2 border-0  placeholder-slate-500 text-slate-900 dark:text-slate-200 focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm placeholder:text-sm`} placeholder="I'm looking for..." value={state.input.search} onChange={onChangeHandler} onBlur={onInputBlur} />

                                                    <div className="w-9 md:w-20 rounded flex-none">
                                                        <DropDown
                                                            type="custom"
                                                            button={
                                                                <>
                                                                    <MenuButton className="relative bg-slate-200 dark:bg-slate-800 rounded-md w-full h-9 px-3 cursor-default focus:outline-none sm:text-sm flex items-center align-middle gap-x-2 justify-center">
                                                                        <span className="fa-light fa-sliders float-end fa-lg"></span>

                                                                        <div className="flex-grow md:block hidden">
                                                                            Filters
                                                                        </div>
                                                                    </MenuButton>
                                                                </>
                                                            }
                                                            items={
                                                                <div className="text-sm w-64 md:h-64 flex flex-col">
                                                                    <div className="w-full px-4 flex-none">
                                                                        <div className="w-full border-b border-slate-300 dark:border-slate-600 py-2">
                                                                            <p className="text-sm text-slate-900 dark:text-slate-100 font-medium">
                                                                                {/* Narrow your search with filters. */}
                                                                                Find it faster with filters.
                                                                            </p>
                                                                        </div>
                                                                    </div>

                                                                    <div className="flex-grow overflow-auto py-3">
                                                                        {
                                                                            state.data.categories.map((category: any, index: any) => {
                                                                                let { input } = state
                                                                                const existsInFilter = input.filters.some(filter => filter.selected === category.uuid);

                                                                                return (
                                                                                    <>
                                                                                        <div key={category.uuid} className="w-full text-start px-4 py-2 text-sm dark:text-slate-300">
                                                                                            <label className="inline-flex items-center" htmlFor={`ctgy-${category.uuid}`}>
                                                                                                <input id={`ctgy-${category.uuid}`} type="checkbox" className="w-4 h-4 accent-orange-600 rounded-md" checked={existsInFilter} name="category" onChange={(e) => handleCheckboxChange(e, category.uuid)} />
                                                                                                <span className="ml-3">{category.name}</span>
                                                                                            </label>
                                                                                        </div>
                                                                                    </>
                                                                                )
                                                                            }
                                                                            )
                                                                        }
                                                                    </div>
                                                                </div>
                                                            }
                                                        />
                                                    </div>

                                                    <button className="flex-none w-9 h-9 bg-primary dark:bg-secondary border border-transparent rounded-md flex items-center justify-center">
                                                        <i className="fa-light fa-arrow-right text-white"></i>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="w-full flex flex-row-reverse py-2 mb-2">
                                            <Link to={categoryRoute} className={`py-1.5 rounded text-sm text-theme hover:text-theme-dark focus:outline-none focus:ring-0 focus:ring-offset-0 focus:ring-primary`}>
                                                <span className="left-0 inset-y-0 flex items-center gap-x-3 align-middle justify-center w-full h-6">
                                                    <i className="fa-light fa-cog fa-lg"></i>
                                                    {pageTitle} Configuration
                                                </span>
                                            </Link>
                                        </div>
                                    </>
                                ) : (
                                    <div className="py-4">
                                        <Loading />
                                    </div>
                                )
                            }
                        </section>
                    </div>
                </div>
            </div>
        </>
    )
}